import { useQueryClient, useIsMutating } from "@tanstack/react-query";
import { RefCallback, useCallback, useMemo } from "react";
import { useOptimistic, startTransition } from "react";

import { rqClient } from "@/shared/api/instance";
import type { ApiSchemas } from "@/shared/api/schema";
import { useSession } from "@/shared/model/session";

// ============================================================================
// Types
// ============================================================================

export type CartItem = ApiSchemas["CartItem"];
type CartItemsResponse = ApiSchemas["CartItemsListResponse"];

export interface CartItemWithActions extends CartItem {
  onUpdateQuantity: (quantity: number) => void;
  onRemove: () => void;
}

// ============================================================================
// Helpers
// ============================================================================

function hasOutOfStockItems(items: CartItem[]): boolean {
  return items.some((item) => !item.product.in_stock);
}

// ============================================================================
// Hook
// ============================================================================

export function useCartModel(limit: number = 20) {
  const queryClient = useQueryClient();
  const { isAuthenticated } = useSession();

  // ============================================================================
  // Cart Items Query (infinite scroll)
  // ============================================================================

  const {
    fetchNextPage,
    data,
    isFetchingNextPage,
    isPending: isPendingItems,
    hasNextPage,
    refetch,
  } = rqClient.useInfiniteQuery(
    "get",
    "/api/v1/cart_items",
    {
      params: {
        query: { page: 1, limit },
      },
    },
    {
      initialPageParam: 1,
      pageParamName: "page",
      getNextPageParam: (
        lastPage: CartItemsResponse,
        _lastPageData: unknown,
        lastPageParams: unknown,
      ) => {
        if (!lastPage.pagination?.pages) return null;
        return Number(lastPageParams) < lastPage.pagination.pages
          ? Number(lastPageParams) + 1
          : null;
      },
      enabled: isAuthenticated,
    },
  );

  // Cart metadata query (totals)
  const {
    data: cartMeta,
    isPending: isPendingMeta,
    isError,
  } = rqClient.useQuery(
    "get",
    "/api/v1/cart",
    {},
    { enabled: isAuthenticated },
  );

  // Flatten items from all pages
  const items: CartItem[] = useMemo(
    () => data?.pages.flatMap((page) => page.data ?? []) ?? [],
    [data],
  );

  // Memoized lookup for cart items by product id (slug)
  const cartItemLookup = useMemo(
    () => Object.fromEntries(items.map((item) => [item.product.id, item])),
    [items],
  );

  // Intersection observer cursor for infinity scroll
  const cursorRef: RefCallback<HTMLDivElement> = useCallback(
    (el) => {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
            fetchNextPage();
          }
        },
        { threshold: 0.5 },
      );

      if (el) {
        observer.observe(el);
        return () => observer.disconnect();
      }
    },
    [fetchNextPage, hasNextPage, isFetchingNextPage],
  );

  // ============================================================================
  // Mutations
  // ============================================================================

  const isAnyCartMutating = useIsMutating({ mutationKey: ["cart"] }) > 0;

  // Track optimistic pending state per product (keyed by slug)
  const [optimisticPending, setOptimisticPending] = useOptimistic<
    Record<string, boolean>
  >({});

  // Add item mutation
  const addMutation = rqClient.useMutation("post", "/api/v1/cart_items", {
    mutationKey: ["cart", "add"],
    onSettled: async () => {
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/cart_items"),
      );
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/cart"),
      );
    },
  });

  // Update item mutation
  const updateMutation = rqClient.useMutation(
    "patch",
    "/api/v1/cart_items/{id}",
    {
      mutationKey: ["cart", "update"],
      onSettled: async () => {
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/cart_items"),
        );
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/cart"),
        );
      },
    },
  );

  // Remove item mutation
  const removeMutation = rqClient.useMutation(
    "delete",
    "/api/v1/cart_items/{id}",
    {
      mutationKey: ["cart", "remove"],
      onSettled: async () => {
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/cart_items"),
        );
        await queryClient.invalidateQueries(
          rqClient.queryOptions("get", "/api/v1/cart"),
        );
      },
    },
  );

  // Clear cart mutation
  const clearMutation = rqClient.useMutation("delete", "/api/v1/cart", {
    mutationKey: ["cart", "clear"],
    onSettled: async () => {
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/cart"),
      );
      await queryClient.invalidateQueries(
        rqClient.queryOptions("get", "/api/v1/cart_items"),
      );
    },
  });

  // ============================================================================
  // Utilities (declared first because actions depend on them)
  // ============================================================================

  const getCartItemId = useCallback(
    (productId: string): string | undefined => {
      return cartItemLookup[productId]?.id;
    },
    [cartItemLookup],
  );

  const getCartItemQuantity = useCallback(
    (productId: string): number => {
      return cartItemLookup[productId]?.quantity ?? 0;
    },
    [cartItemLookup],
  );

  const isItemPending = useCallback(
    (slug: string) => optimisticPending[slug] ?? false,
    [optimisticPending],
  );

  // ============================================================================
  // Actions
  // ============================================================================

  const addItem = useCallback(
    (productId: string, quantity: number = 1) => {
      startTransition(async () => {
        setOptimisticPending((prev) => ({ ...prev, [productId]: true }));
        try {
          await addMutation.mutateAsync({
            body: { cart_item: { product_slug: productId, quantity } },
          });
        } finally {
          setOptimisticPending((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        }
      });
    },
    [addMutation, setOptimisticPending],
  );

  const removeItem = useCallback(
    (productId: string) => {
      const cartItemId = getCartItemId(productId);
      if (!cartItemId) return;

      startTransition(async () => {
        setOptimisticPending((prev) => ({ ...prev, [productId]: true }));
        try {
          await removeMutation.mutateAsync({
            params: { path: { id: cartItemId } },
          });
        } finally {
          setOptimisticPending((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        }
      });
    },
    [removeMutation, setOptimisticPending, getCartItemId],
  );

  const setQuantity = useCallback(
    (productId: string, quantity: number) => {
      if (quantity <= 0) {
        removeItem(productId);
        return;
      }

      const cartItemId = getCartItemId(productId);

      startTransition(async () => {
        setOptimisticPending((prev) => ({ ...prev, [productId]: true }));
        try {
          if (cartItemId) {
            // Item exists in cart - update quantity
            await updateMutation.mutateAsync({
              params: { path: { id: cartItemId } },
              body: { cart_item: { quantity } },
            });
          } else {
            // Item not in cart - add with quantity
            await addMutation.mutateAsync({
              body: { cart_item: { product_slug: productId, quantity } },
            });
          }
        } finally {
          setOptimisticPending((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        }
      });
    },
    [
      addMutation,
      updateMutation,
      setOptimisticPending,
      removeItem,
      getCartItemId,
    ],
  );

  const updateQuantity = useCallback(
    (productId: string, quantity: number) => {
      if (quantity <= 0) {
        removeItem(productId);
        return;
      }
      const cartItemId = getCartItemId(productId);
      if (!cartItemId) return;

      startTransition(async () => {
        setOptimisticPending((prev) => ({ ...prev, [productId]: true }));
        try {
          await updateMutation.mutateAsync({
            params: { path: { id: cartItemId } },
            body: { cart_item: { quantity } },
          });
        } finally {
          setOptimisticPending((prev) => {
            const next = { ...prev };
            delete next[productId];
            return next;
          });
        }
      });
    },
    [updateMutation, setOptimisticPending, removeItem, getCartItemId],
  );

  const clearCart = useCallback(() => {
    clearMutation.mutate({});
  }, [clearMutation]);

  // ============================================================================
  // Items with Actions (for view model)
  // ============================================================================

  const itemsWithActions: CartItemWithActions[] = useMemo(
    () =>
      items.map((item) => ({
        ...item,
        onUpdateQuantity: (qty: number) => updateQuantity(item.product.id, qty),
        onRemove: () => removeItem(item.product.id),
      })),
    [items, updateQuantity, removeItem],
  );

  // ============================================================================
  // Return
  // ============================================================================

  const isPending = isPendingItems || isPendingMeta;
  const isEmpty = items.length === 0 && !isPending;
  const hasOutOfStock = hasOutOfStockItems(items);
  const isMutating =
    addMutation.isPending ||
    updateMutation.isPending ||
    removeMutation.isPending ||
    clearMutation.isPending;

  return {
    // Items
    items: itemsWithActions,
    rawItems: items,
    isEmpty,
    hasOutOfStock,

    // Infinity scroll
    cursorRef,
    isFetchingNextPage,
    hasNextPage,

    // Cart summary
    totals: {
      totalCents: cartMeta?.total ?? 0,
      itemCount: cartMeta?.item_count ?? 0,
      productCount: cartMeta?.product_count ?? 0,
    },

    // Actions
    addItem,
    setQuantity,
    updateQuantity,
    removeItem,
    clearCart,
    refetch,

    // Utilities
    getCartItemId,
    getCartItemQuantity,
    isItemPending,

    // State
    isPending,
    isError,
    isMutating,
    isAnyMutating: isAnyCartMutating,
  };
}

export type CartModel = ReturnType<typeof useCartModel>;
